import { useCallback, useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * Registriert den Service Worker und meldet, wenn eine neue Version bereit
 * liegt.
 *
 * Bewusst mit Rueckfrage statt stiller Uebernahme: ein Wechsel mitten im
 * Tippen laedt die Seite neu und wirft die halb geschriebene Karte weg. Der
 * Hinweis wartet, bis jemand ihn antippt.
 */
export function useServiceWorker(): { updateReady: boolean; applyUpdate: () => void } {
  const [updateReady, setUpdateReady] = useState(false);
  const updateSW = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    updateSW.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setUpdateReady(true),
    });
  }, []);

  const applyUpdate = useCallback(() => {
    setUpdateReady(false);
    void updateSW.current?.(true);
  }, []);

  return { updateReady, applyUpdate };
}

/**
 * Ob das Geraet nach eigener Auskunft online ist.
 *
 * `navigator.onLine` sagt nur, dass eine Netzwerkschnittstelle da ist — ein
 * WLAN ohne Internet meldet weiterhin `true`. Fuer das Banner reicht das: der
 * Fall, den es abfangen soll, ist der Flugmodus und das Funkloch. Ob die API
 * tatsaechlich antwortet, zeigt der Verbindungszustand am Board.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}
